// frontend/src/pages/AlertsPage.jsx
/**
 * Sprint 13.2 — Configuración Global de Alertas
 *
 * Página accesible desde Sidebar → "Alertas".
 * V1: configuración global (idConfigHead=null → aplica a todos los pipelines).
 * V2 futuro: configuración por pipeline individual.
 *
 * Cada tipo de alerta se muestra como una tarjeta con toggle on/off,
 * campo de threshold editable (si aplica), y lista de recipients.
 *
 * UX de recipients: input con emails separados por comas
 * (mismo patrón que NotificationPanel en StepReview).
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Bell, Shield, Activity, HeartPulse, XCircle, Clock, ShieldBan,
  X, Save, Loader2, Menu, Check, Trash2, Mail, Undo2, LogOut,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiClient } from '../api/client';
import { useLanguageStore } from '../store/useLanguageStore';
import { useUIStore } from '../store/useUIStore';
import { AppSidebar } from '../components/AppSidebar';
import { LanguageSelector } from '../components/LanguageSelector';
import LogoHorizontal from '../assets/images/datiaFlow_horizontal.jpg';
import Isotipo from '../assets/images/datiaFlow_cuadrado.jpg';

// ── Configuración de tipos de alerta ──────────────────────────

const ALERT_TYPE_CONFIG = {
  schema_drift: {
    icon: Shield,
    color: 'indigo',
    thresholdFields: [],
  },
  sync_discrepancy: {
    icon: Activity,
    color: 'amber',
    thresholdFields: [
      { key: 'maxDiscrepancyPercent', type: 'number', default: 5, min: 1, max: 50, suffix: '%' },
    ],
  },
  low_health_score: {
    icon: HeartPulse,
    color: 'rose',
    thresholdFields: [
      { key: 'minScore', type: 'number', default: 60, min: 10, max: 100, suffix: '/100' },
    ],
  },
  execution_failed: {
    icon: XCircle,
    color: 'red',
    thresholdFields: [],
  },
  stale_pipeline: {
    icon: Clock,
    color: 'slate',
    thresholdFields: [
      { key: 'maxInactiveDays', type: 'number', default: 7, min: 1, max: 90, suffix: '' },
    ],
  },
  quality_violation: {
    icon: ShieldBan,
    color: 'emerald',
    thresholdFields: [
      { key: 'maxBlockedRows', type: 'number', default: 10, min: 1, max: 100000, suffix: '' },
    ],
  },
};

const MAX_RECIPIENTS = 10;

const ALERT_TYPES_ORDER = [
  'execution_failed',
  'sync_discrepancy',
  'low_health_score',
  'schema_drift',
  'stale_pipeline',
  'quality_violation',
];

// ── Helpers ───────────────────────────────────────────────────

function _getDefaultThreshold(type) {
  const defaults = {
    schema_drift: {},
    sync_discrepancy: { maxDiscrepancyPercent: 5 },
    low_health_score: { minScore: 60 },
    execution_failed: {},
    stale_pipeline: { maxInactiveDays: 7 },
    quality_violation: { maxBlockedRows: 10 },
  };
  return defaults[type] || {};
}

/** Parsea string de emails separados por comas → array limpio */
function _parseEmails(raw) {
  return raw
    .split(',')
    .map(e => e.trim().toLowerCase())
    .filter(e => e.length > 0);
}

/** Valida un email con regex básico */
function _isValidEmail(email) {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
}


// ── Componente principal ──────────────────────────────────────

export const AlertsPage = ({ user, setUser }) => {
  const { t } = useLanguageStore();
  const { openSidebar } = useUIStore();

  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Estado local editable (por tipo)
  const [localState, setLocalState] = useState({});
  // Snapshot para poder hacer "Cancelar" y revertir
  const [snapshot, setSnapshot] = useState({});
  const [clientName, setClientName] = useState('');

  // Cargar nombre del cliente
  useEffect(() => {
    apiClient.get('/dashboard/pipelines').then(res => {
      if (res.data.clientName) setClientName(res.data.clientName);
    }).catch(() => { });
  }, []);

  // ── Cargar alertas existentes ─────────────────────────────

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/alerts/');
      const globalAlerts = (res.data || []).filter(a => a.idConfigHead === null);
      setAlerts(globalAlerts);

      const state = {};
      for (const type of ALERT_TYPES_ORDER) {
        const existing = globalAlerts.find(a => a.alertType === type);
        if (existing) {
          state[type] = {
            id: existing.idAlertConfig,
            enabled: existing.enabled === 1,
            threshold: existing.threshold || _getDefaultThreshold(type),
            recipientsRaw: (existing.recipients || []).join(', '),
            exists: true,
            dirty: false,
          };
        } else {
          state[type] = {
            id: null,
            enabled: false,
            threshold: _getDefaultThreshold(type),
            recipientsRaw: user?.email || '',
            exists: false,
            dirty: false,
          };
        }
      }
      setLocalState(state);
      setSnapshot(JSON.parse(JSON.stringify(state)));
    } catch (err) {
      setError(t('alerting.load_error') || 'Error cargando alertas');
    } finally {
      setLoading(false);
    }
  }, [t, user]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  // ── Guardar una alerta ────────────────────────────────────

  const handleSave = async (alertType) => {
    const state = localState[alertType];
    if (!state) return;

    const emails = _parseEmails(state.recipientsRaw);
    const invalidEmails = emails.filter(e => !_isValidEmail(e));

    if (state.enabled && emails.length === 0) {
      setError(t('alerting.no_recipients') || 'Agrega al menos un destinatario');
      return;
    }
    if (emails.length > MAX_RECIPIENTS) {
      setError(
        (t('alerting.max_recipients') || `Máximo ${MAX_RECIPIENTS} destinatarios`)
          .replace('{max}', MAX_RECIPIENTS)
      );
      return;
    }
    if (invalidEmails.length > 0) {
      setError(
        (t('alerting.invalid_email') || 'Email inválido') +
        ': ' + invalidEmails.join(', ')
      );
      return;
    }

    setSaving(alertType);
    setError(null);

    try {
      if (state.exists && state.id) {
        await apiClient.put(`/alerts/${state.id}`, {
          enabled: state.enabled ? 1 : 0,
          threshold: state.threshold,
          recipients: emails,
        });
      } else if (state.enabled) {
        await apiClient.post('/alerts/', {
          idConfigHead: null,
          alertType,
          enabled: 1,
          threshold: state.threshold,
          channels: ['email'],
          recipients: emails,
        });
      }

      setSuccessMsg(t('alerting.saved') || 'Alerta guardada correctamente');
      setTimeout(() => setSuccessMsg(null), 3000);
      await fetchAlerts();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Error guardando alerta';
      setError(msg);
    } finally {
      setSaving(null);
    }
  };

  // ── Cancelar edición (revertir al snapshot) ───────────────

  const handleCancel = (alertType) => {
    if (!snapshot[alertType]) return;
    setLocalState(prev => ({
      ...prev,
      [alertType]: { ...JSON.parse(JSON.stringify(snapshot[alertType])), dirty: false },
    }));
    setError(null);
  };

  // ── Eliminar una alerta ───────────────────────────────────

  const handleDelete = async (alertType) => {
    const state = localState[alertType];
    if (!state?.exists || !state.id) return;

    try {
      await apiClient.delete(`/alerts/${state.id}`);
      setSuccessMsg(t('alerting.deleted') || 'Alerta eliminada');
      setTimeout(() => setSuccessMsg(null), 3000);
      await fetchAlerts();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error eliminando alerta');
    }
  };

  // ── Handlers de estado local ──────────────────────────────

  const updateLocal = (type, field, value) => {
    setLocalState(prev => ({
      ...prev,
      [type]: { ...prev[type], [field]: value, dirty: true },
    }));
  };

  const updateThreshold = (type, key, value) => {
    setLocalState(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        threshold: { ...(prev[type]?.threshold || {}), [key]: value },
        dirty: true,
      },
    }));
  };

  // ── Logout ────────────────────────────────────────────────

  const handleLogout = () => {
    localStorage.removeItem('vnext_auth_token');
    localStorage.removeItem('vnext_user');
    setUser(null);
  };

  // ── Render ────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <AppSidebar user={user} onLogout={handleLogout} />

      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={openSidebar}
              aria-label="Abrir menú"
              className="p-2 rounded-xl text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 transition-colors shrink-0"
            >
              <Menu className="w-5 h-5" />
            </button>
            <Link to="/dashboard" className="flex items-center cursor-pointer group shrink-0">
              <img src={Isotipo} alt="DatiaFlow"
                className="block md:hidden h-9 w-9 object-cover object-left rounded-md group-hover:scale-105 transition-transform" />
              <img src={LogoHorizontal} alt="DatiaFlow"
                className="hidden md:block h-11 w-auto group-hover:opacity-90 transition-opacity" />
            </Link>
          </div>
          <div className="flex items-center space-x-6">
            <LanguageSelector />
            <div className="flex items-center space-x-3 border-l border-slate-200 pl-6">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-bold text-slate-700">{clientName}</p>
                <p className="text-xs text-slate-500">{user?.name}</p>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                title={t('dashboard.logout')}
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        {/* Título */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-indigo-50 rounded-xl">
              <Bell className="w-5 h-5 text-indigo-600" />
            </div>
            <h1 className="text-xl font-bold text-slate-900">
              {t('alerting.title') || 'Alertas'}
            </h1>
          </div>
          <p className="text-sm text-slate-500 ml-12">
            {t('alerting.subtitle') || 'Recibe notificaciones cuando tus pipelines necesiten atención.'}
          </p>
        </div>

        {/* Mensajes */}
        {error && (
          <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex items-center gap-2">
            <XCircle className="w-4 h-4 shrink-0" />
            <span className="flex-1">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        {successMsg && (
          <div className="mb-4 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-700 flex items-center gap-2">
            <Check className="w-4 h-4 shrink-0" />
            {successMsg}
          </div>
        )}

        {/* Loading */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
          </div>
        ) : (
          <div className="space-y-4">
            {ALERT_TYPES_ORDER.map(alertType => (
              <AlertCard
                key={alertType}
                alertType={alertType}
                state={localState[alertType]}
                saving={saving === alertType}
                t={t}
                onToggle={(v) => updateLocal(alertType, 'enabled', v)}
                onThresholdChange={(k, v) => updateThreshold(alertType, k, v)}
                onRecipientsChange={(v) => updateLocal(alertType, 'recipientsRaw', v)}
                onSave={() => handleSave(alertType)}
                onCancel={() => handleCancel(alertType)}
                onDelete={() => handleDelete(alertType)}
              />
            ))}
          </div>
        )}

        {/* Footer info */}
        <div className="mt-10 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl">
          <p className="text-xs text-slate-500">
            {t('alerting.footer_info') || 'Las alertas globales aplican a todos los pipelines de tu cuenta. La configuración por pipeline individual estará disponible próximamente.'}
          </p>
        </div>
      </main>
    </div>
  );
};


// ── AlertCard ─────────────────────────────────────────────────

const AlertCard = ({
  alertType, state, saving, t,
  onToggle, onThresholdChange, onRecipientsChange,
  onSave, onCancel, onDelete,
}) => {
  if (!state) return null;

  const config = ALERT_TYPE_CONFIG[alertType] || {};
  const Icon = config.icon || Bell;
  const colorMap = {
    indigo: { bg: 'bg-indigo-50', text: 'text-indigo-600', border: 'border-indigo-200' },
    amber: { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200' },
    rose: { bg: 'bg-rose-50', text: 'text-rose-600', border: 'border-rose-200' },
    red: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200' },
    slate: { bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200' },
  };
  const colors = colorMap[config.color] || colorMap.slate;

  // Contar emails válidos para el indicator
  const emailCount = _parseEmails(state.recipientsRaw || '').filter(_isValidEmail).length;

  return (
    <div className={`bg-white border rounded-2xl overflow-hidden transition-all
      ${state.enabled ? 'border-indigo-200 shadow-sm' : 'border-slate-200'}`}
    >
      {/* Header */}
      <div className="px-5 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-xl ${colors.bg}`}>
            <Icon className={`w-4 h-4 ${colors.text}`} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-800">
              {t(`alerting.type_${alertType}`) || alertType.replace(/_/g, ' ')}
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">
              {t(`alerting.desc_${alertType}`) || ''}
            </p>
          </div>
        </div>

        {/* Toggle */}
        <button
          onClick={() => onToggle(!state.enabled)}
          className={`relative w-11 h-6 rounded-full transition-colors duration-200
            ${state.enabled ? 'bg-indigo-500' : 'bg-slate-200'}`}
        >
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full
            shadow transition-transform duration-200
            ${state.enabled ? 'translate-x-5' : 'translate-x-0'}`}
          />
        </button>
      </div>

      {/* Expanded config (when enabled) */}
      {state.enabled && (
        <div className="px-5 pb-5 pt-1 border-t border-slate-100 space-y-4">

          {/* Threshold fields */}
          {(config.thresholdFields || []).length > 0 && (
            <div className="space-y-2">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                {t('alerting.threshold') || 'Umbral'}
              </label>
              {config.thresholdFields.map(field => (
                <div key={field.key} className="flex items-center gap-2">
                  <span className="text-xs text-slate-600 min-w-[120px]">
                    {t(`alerting.threshold_${field.key}`) || field.key}
                  </span>
                  <input
                    type="number"
                    min={field.min}
                    max={field.max}
                    value={state.threshold?.[field.key] ?? field.default}
                    onChange={(e) => {
                      let v = Number(e.target.value);
                      if (field.min != null && v < field.min) v = field.min;
                      if (field.max != null && v > field.max) v = field.max;
                      onThresholdChange(field.key, v);
                    }}
                    className="w-20 px-2 py-1.5 text-sm border border-slate-200 rounded-lg
                               focus:outline-none focus:ring-2 focus:ring-indigo-300 text-center"
                  />
                  {field.suffix && (
                    <span className="text-xs text-slate-400">{field.suffix}</span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Recipients — comma-separated input (mismo UX que NotificationPanel) */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
              <Mail className="w-3 h-3" />
              {t('alerting.recipients') || 'Destinatarios'}
              {emailCount > 0 && (
                <span className="text-[10px] font-bold text-indigo-500 bg-indigo-50 px-1.5 py-0.5 rounded-full ml-1">
                  {emailCount}
                </span>
              )}
            </label>
            <textarea
              rows={2}
              value={state.recipientsRaw || ''}
              onChange={(e) => {
                onRecipientsChange(e.target.value);
                // Auto-resize: ajusta la altura al contenido
                e.target.style.height = 'auto';
                e.target.style.height = e.target.scrollHeight + 'px';
              }}
              placeholder="admin@empresa.com, cto@empresa.com"
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg
                         focus:outline-none focus:ring-2 focus:ring-indigo-300
                         placeholder:text-slate-300 resize-none leading-relaxed"
            />
            <div className="flex items-center justify-between">
              <p className="text-[11px] text-slate-400">
                {t('alerting.recipients_hint') || 'Separa los correos con comas.'}
              </p>
              <span className={`text-[11px] font-medium ${emailCount > MAX_RECIPIENTS ? 'text-red-500' : 'text-slate-400'}`}>
                {emailCount}/{MAX_RECIPIENTS}
              </span>
            </div>
          </div>

          {/* Actions: Cancelar + Guardar (+ Eliminar si existe) */}
          <div className="flex items-center justify-between pt-2">
            <div>
              {state.exists && (
                <button
                  onClick={onDelete}
                  className="text-xs text-slate-400 hover:text-red-500 flex items-center gap-1 transition"
                >
                  <Trash2 className="w-3 h-3" />
                  {t('alerting.delete') || 'Eliminar'}
                </button>
              )}
            </div>

            <div className="flex items-center gap-2">
              {/* Cancelar — solo visible si hay cambios */}
              {state.dirty && (
                <button
                  onClick={onCancel}
                  className="px-4 py-2 text-xs font-semibold text-slate-600
                             border border-slate-200 hover:bg-slate-50
                             rounded-xl transition flex items-center gap-1.5"
                >
                  <Undo2 className="w-3.5 h-3.5" />
                  {t('common.buttons.cancel') || 'Cancelar'}
                </button>
              )}

              {/* Guardar */}
              <button
                onClick={onSave}
                disabled={saving}
                className="px-4 py-2 text-xs font-semibold text-white bg-indigo-600
                           hover:bg-indigo-700 rounded-xl transition disabled:opacity-50
                           flex items-center gap-1.5"
              >
                {saving
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <Save className="w-3.5 h-3.5" />
                }
                {t('common.buttons.save') || 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default AlertsPage;
